import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ROLES } from '../utils/roles';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  Grid,
  TextField,
  Paper,
  Divider,
  Chip,
  Alert,
  IconButton,
  Collapse
} from '@mui/material';
import { ExpandMore, ExpandLess, Visibility, Build, SmartToy } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { it } from 'date-fns/locale';
import { Preview, Print } from '@mui/icons-material';
import api from '../utils/api';
import MaintenanceDetails from './maintenance/MaintenanceDetails';
import MaintenanceRepair from './maintenance/MaintenanceRepair';

const ReportDialog = ({ open, onClose }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [selectedSectors, setSelectedSectors] = useState({
    maintenance: false,
    sales: false,
    inventory: false,
    mechanics: false
  });

  const [selectedMetrics, setSelectedMetrics] = useState({
    revenue: false,
    costs: false,
    margins: false,
    quantities: false,
    times: false,
    payments: false
  });

  const [dateRange, setDateRange] = useState({
    startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1), // Primo del mese corrente
    endDate: new Date()
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [reportData, setReportData] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [expandedMechanics, setExpandedMechanics] = useState({});
  const [expandedDays, setExpandedDays] = useState({});
  const [expandedDescriptions, setExpandedDescriptions] = useState({});
  const [maintenanceDetailsOpen, setMaintenanceDetailsOpen] = useState(false);
  const [maintenanceRepairOpen, setMaintenanceRepairOpen] = useState(false);
  const [selectedMaintenanceId, setSelectedMaintenanceId] = useState(null);
  const [reportDialogOpen, setReportDialogOpen] = useState(true); // State to control main report dialog

  const sectors = [
    { key: 'maintenance', label: 'Manutenzioni', icon: '🔧' },
    { key: 'sales', label: 'Vendite', icon: '🛒' },
    { key: 'rentals', label: 'Noleggi', icon: '🚗' },
    { key: 'inventory', label: 'Magazzino', icon: '📦' },
    { key: 'mechanics', label: 'Meccanici', icon: '👷' }
  ];

  const metrics = [
    { key: 'revenue', label: 'Fatturato', icon: '💰' },
    { key: 'costs', label: 'Costi', icon: '💸' },
    { key: 'margins', label: 'Margini', icon: '📈' },
    { key: 'quantities', label: 'Quantità', icon: '📊' },
    { key: 'times', label: 'Tempi', icon: '⏱️' },
    { key: 'payments', label: 'Pagamenti', icon: '💳' }
  ];

  const handleSectorChange = (sectorKey) => {
    setSelectedSectors(prev => ({
      ...prev,
      [sectorKey]: !prev[sectorKey]
    }));
  };

  const handleMetricChange = (metricKey) => {
    setSelectedMetrics(prev => ({
      ...prev,
      [metricKey]: !prev[metricKey]
    }));
  };

  const handleOpenAiChat = () => {
    onClose();
    navigate('/dashboard/ai-chat');
  };

  const toggleMechanicExpansion = (mechanicId) => {
    setExpandedMechanics(prev => ({
      ...prev,
      [mechanicId]: !prev[mechanicId]
    }));
  };

  const toggleDayExpansion = (mechanicId, date) => {
    const key = `${mechanicId}-${date}`;
    setExpandedDays(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const toggleDescriptionExpansion = (maintenanceId) => {
    setExpandedDescriptions(prev => ({
      ...prev,
      [maintenanceId]: !prev[maintenanceId]
    }));
  };

  const handleOpenMaintenanceDetails = (maintenanceId) => {
    setSelectedMaintenanceId(maintenanceId);
    setMaintenanceDetailsOpen(true);
    setReportDialogOpen(false); // Chiude il dialog principale
  };

  const handleOpenMaintenanceRepair = (maintenanceId) => {
    setSelectedMaintenanceId(maintenanceId);
    setMaintenanceRepairOpen(true);
    setReportDialogOpen(false); // Chiude il dialog principale
  };

  const handleCloseMaintenanceDetails = () => {
    setMaintenanceDetailsOpen(false);
    setSelectedMaintenanceId(null);
    setReportDialogOpen(true); // Riapre il dialog principale
  };

  const handleCloseMaintenanceRepair = () => {
    setMaintenanceRepairOpen(false);
    setSelectedMaintenanceId(null);
    setReportDialogOpen(true); // Riapre il dialog principale
  };

  const handleGeneratePreview = async () => {
    // Validazione
    const hasSelectedSectors = Object.values(selectedSectors).some(Boolean);
    const hasSelectedMetrics = Object.values(selectedMetrics).some(Boolean);

    if (!hasSelectedSectors) {
      setError('Seleziona almeno un settore da analizzare');
      return;
    }

    if (!hasSelectedMetrics) {
      setError('Seleziona almeno una metrica da includere');
      return;
    }

    if (!dateRange.startDate || !dateRange.endDate) {
      setError('Seleziona un periodo valido');
      return;
    }

    setError('');
    setLoading(true);

    try {
      console.log('📊 Generazione report - parametri:', {
        sectors: selectedSectors,
        metrics: selectedMetrics,
        startDate: dateRange.startDate.toISOString(),
        endDate: dateRange.endDate.toISOString()
      });

      const response = await api.post('/reports/generate-data', {
        sectors: selectedSectors,
        metrics: selectedMetrics,
        startDate: dateRange.startDate.toISOString(),
        endDate: dateRange.endDate.toISOString()
      });

      console.log('📊 Dati ricevuti:', response.data);
      setReportData(response.data);
      setShowPreview(true);
    } catch (err) {
      console.error('❌ Errore nella generazione del report:', err);
      setError(err.response?.data?.error || 'Errore nella generazione del report. Riprova.');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleClose = () => {
    setError('');
    setShowPreview(false);
    setReportData(null);
    onClose();
  };

  const handleBackToSettings = () => {
    setShowPreview(false);
    setReportData(null);
  };

  const getSelectedCount = (obj) => Object.values(obj).filter(Boolean).length;

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('it-IT');
  };

  if (showPreview && reportData) {
    return (
      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={it}>
        {/* Dialog di preview - si nasconde quando reportDialogOpen è false */}
        <Dialog
          open={open && reportDialogOpen}
          onClose={handleClose}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: { borderRadius: 2, height: '90vh' }
          }}
        >
        <DialogTitle sx={{ pb: 1, '@media print': { display: 'none' } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h5">Anteprima Report</Typography>
            <Box>
              <Button onClick={handleBackToSettings} sx={{ mr: 1 }}>
                Modifica
              </Button>
              <Button
                variant="contained"
                startIcon={<Print />}
                onClick={handlePrint}
              >
                Stampa
              </Button>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          <div id="report-content" style={{ fontFamily: 'Arial, sans-serif' }}>
            {/* Header Report */}
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Typography variant="h3" gutterBottom>
                Report Aziendale
              </Typography>
              <Typography variant="h6" color="text.secondary">
                Periodo: {formatDate(dateRange.startDate)} - {formatDate(dateRange.endDate)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Generato il {new Date().toLocaleDateString('it-IT')} alle {new Date().toLocaleTimeString('it-IT')}
              </Typography>
            </Box>

            <Divider sx={{ mb: 3 }} />

            {/* Sezione Manutenzioni */}
            {selectedSectors.maintenance && reportData.maintenance && (
              <Box sx={{ mb: 4 }}>
                <Typography variant="h4" gutterBottom color="primary">
                  🔧 MANUTENZIONI
                </Typography>
                <Grid container spacing={2}>
                  {selectedMetrics.quantities && (
                    <>
                      <Grid item xs={12} sm={6}>
                        <Paper sx={{ p: 2, textAlign: 'center' }}>
                          <Typography variant="h5" color="primary">
                            {reportData.maintenance.count}
                          </Typography>
                          <Typography variant="body2">Totale Manutenzioni</Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Paper sx={{ p: 2, textAlign: 'center' }}>
                          <Typography variant="h5" color="success.main">
                            {reportData.maintenance.completed}
                          </Typography>
                          <Typography variant="body2">Manutenzioni Completate</Typography>
                        </Paper>
                      </Grid>
                    </>
                  )}
                  {selectedMetrics.revenue && (
                    <Grid item xs={12}>
                                              <Paper sx={{ p: 2, textAlign: 'center' }}>
                          <Typography variant="h5" color="success.main">
                            {formatCurrency(reportData.maintenance.totalRevenue)}
                          </Typography>
                          <Typography variant="body2">Fatturato Totale (esclusa IVA 22%)</Typography>
                          <Typography variant="caption" color="text.secondary">
                            Con IVA: {formatCurrency(reportData.maintenance.totalRevenueWithVAT)}
                          </Typography>
                        </Paper>
                    </Grid>
                  )}
                  {selectedMetrics.costs && (
                    <>
                      <Grid item xs={12}>
                        <Paper sx={{ p: 2, textAlign: 'center' }}>
                          <Typography variant="h5" color="error.main">
                            {formatCurrency(reportData.maintenance.totalCosts)}
                          </Typography>
                          <Typography variant="body2">Costi Totali</Typography>
                        </Paper>
                      </Grid>

                      <Grid item xs={12} sm={4}>
                        <Paper sx={{ p: 2, textAlign: 'center' }}>
                          <Typography variant="h6" color="error.main">
                            {formatCurrency(reportData.maintenance.totalLaborCost)}
                          </Typography>
                          <Typography variant="body2">Costi Manodopera</Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Paper sx={{ p: 2, textAlign: 'center' }}>
                          <Typography variant="h6" color="error.main">
                            {formatCurrency(reportData.maintenance.totalMaterialCost)}
                          </Typography>
                          <Typography variant="body2">Costi Materiali</Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Paper sx={{ p: 2, textAlign: 'center' }}>
                          <Typography variant="h6" color="error.main">
                            {formatCurrency(reportData.maintenance.totalOverheadCost)}
                          </Typography>
                          <Typography variant="body2">Spese Generali 20% </Typography>
                        </Paper>
                      </Grid>
                    </>
                  )}
                  {selectedMetrics.margins && (
                    <Grid item xs={12}>
                      <Paper sx={{ p: 2, textAlign: 'center' }}>
                        <Typography variant="h5" color={reportData.maintenance.totalMargins >= 0 ? "success.main" : "error.main"}>
                          {formatCurrency(reportData.maintenance.totalMargins)}
                        </Typography>
                        <Typography variant="body2">Margini Totali (Fatturato - Costi)</Typography>
                      </Paper>
                    </Grid>
                  )}
                  {selectedMetrics.times && (
                    <Grid item xs={12}>
                      <Paper sx={{ p: 2, textAlign: 'center' }}>
                        <Typography variant="h5" color="warning.main">
                          {reportData.maintenance.avgCompletionTime?.toFixed(1) || 0} giorni
                        </Typography>
                        <Typography variant="body2">Tempo Medio Completamento</Typography>
                      </Paper>
                    </Grid>
                  )}
                </Grid>
              </Box>
            )}

            {/* Sezione Vendite */}
            {selectedSectors.sales && reportData.sales && (
              <Box sx={{ mb: 4 }}>
                <Typography variant="h4" gutterBottom color="primary">
                  🛒 VENDITE
                </Typography>
                <Grid container spacing={2}>
                  {selectedMetrics.quantities && (
                    <>
                      <Grid item xs={12} sm={4}>
                        <Paper sx={{ p: 2, textAlign: 'center' }}>
                          <Typography variant="h5" color="primary">
                            {reportData.sales.count}
                          </Typography>
                          <Typography variant="body2">Totale Vendite</Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Paper sx={{ p: 2, textAlign: 'center' }}>
                          <Typography variant="h5" color="success.main">
                            {reportData.sales.completed}
                          </Typography>
                          <Typography variant="body2">Vendite Completate</Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Paper sx={{ p: 2, textAlign: 'center' }}>
                          <Typography variant="h5" color="info.main">
                            {reportData.sales.totalItems}
                          </Typography>
                          <Typography variant="body2">Articoli Venduti</Typography>
                        </Paper>
                      </Grid>
                    </>
                  )}
                  {selectedMetrics.revenue && (
                    <>
                      <Grid item xs={12} sm={6}>
                        <Paper sx={{ p: 2, textAlign: 'center' }}>
                          <Typography variant="h5" color="success.main">
                            {formatCurrency(reportData.sales.totalRevenue)}
                          </Typography>
                          <Typography variant="body2">Fatturato Totale (esclusa IVA 22%)</Typography>
                          <Typography variant="caption" color="text.secondary">
                            Con IVA: {formatCurrency(reportData.sales.totalRevenueWithVAT)}
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Paper sx={{ p: 2, textAlign: 'center' }}>
                          <Typography variant="h5" color="warning.main">
                            {formatCurrency(reportData.sales.avgOrderValue)}
                          </Typography>
                          <Typography variant="body2">Valore Medio Ordine (esclusa IVA)</Typography>
                        </Paper>
                      </Grid>
                    </>
                  )}
                  {selectedMetrics.costs && (
                    <Grid item xs={12}>
                      <Paper sx={{ p: 2, textAlign: 'center' }}>
                        <Typography variant="h5" color="error.main">
                          {formatCurrency(reportData.sales.totalCosts)}
                        </Typography>
                        <Typography variant="body2">Costi Totali Prodotti</Typography>
                      </Paper>
                    </Grid>
                  )}
                  {selectedMetrics.margins && (
                    <>
                      <Grid item xs={12} sm={6}>
                        <Paper sx={{ p: 2, textAlign: 'center' }}>
                          <Typography variant="h5" color={reportData.sales.totalMargins >= 0 ? "success.main" : "error.main"}>
                            {formatCurrency(reportData.sales.totalMargins)}
                          </Typography>
                          <Typography variant="body2">Margini Totali</Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Paper sx={{ p: 2, textAlign: 'center' }}>
                          <Typography variant="h5" color={reportData.sales.avgMarginPerSale >= 0 ? "success.main" : "error.main"}>
                            {formatCurrency(reportData.sales.avgMarginPerSale)}
                          </Typography>
                          <Typography variant="body2">Margine Medio per Vendita</Typography>
                        </Paper>
                      </Grid>
                    </>
                  )}
                </Grid>
              </Box>
            )}

            {/* Sezione Noleggi */}
            {selectedSectors.rentals && reportData.rentals && (
              <Box sx={{ mb: 4 }}>
                <Typography variant="h4" gutterBottom color="primary">
                  🚗 NOLEGGI
                </Typography>
                <Grid container spacing={2}>
                  {selectedMetrics.revenue && (
                    <>
                      <Grid item xs={12} sm={6}>
                        <Paper sx={{ p: 2, textAlign: 'center' }}>
                          <Typography variant="h5" color="success.main">
                            {formatCurrency(reportData.rentals.totalRevenue)}
                          </Typography>
                          <Typography variant="body2">Fatturato Totale (esclusa IVA 22%)</Typography>
                          <Typography variant="caption" color="text.secondary">
                            Con IVA: {formatCurrency(reportData.rentals.totalRevenueWithVAT)}
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Paper sx={{ p: 2, textAlign: 'center' }}>
                          <Typography variant="h5" color="warning.main">
                            {formatCurrency(reportData.rentals.avgRentalValue)}
                          </Typography>
                          <Typography variant="body2">Valore Medio Noleggio</Typography>
                        </Paper>
                      </Grid>
                    </>
                  )}
                  {selectedMetrics.counts && (
                    <>
                      <Grid item xs={12} sm={4}>
                        <Paper sx={{ p: 2, textAlign: 'center' }}>
                          <Typography variant="h5" color="primary.main">
                            {reportData.rentals.count}
                          </Typography>
                          <Typography variant="body2">Noleggi Totali</Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Paper sx={{ p: 2, textAlign: 'center' }}>
                          <Typography variant="h5" color="success.main">
                            {reportData.rentals.completed}
                          </Typography>
                          <Typography variant="body2">Noleggi Completati</Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Paper sx={{ p: 2, textAlign: 'center' }}>
                          <Typography variant="h5" color="warning.main">
                            {reportData.rentals.active}
                          </Typography>
                          <Typography variant="body2">Noleggi Attivi</Typography>
                        </Paper>
                      </Grid>
                    </>
                  )}
                  {selectedMetrics.times && (
                    <>
                      <Grid item xs={12} sm={6}>
                        <Paper sx={{ p: 2, textAlign: 'center' }}>
                          <Typography variant="h5" color="info.main">
                            {reportData.rentals.totalDays}
                          </Typography>
                          <Typography variant="body2">Giorni Totali Noleggio</Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Paper sx={{ p: 2, textAlign: 'center' }}>
                          <Typography variant="h5" color="info.main">
                            {reportData.rentals.avgRentalDuration?.toFixed(1) || 0} giorni
                          </Typography>
                          <Typography variant="body2">Durata Media Noleggio</Typography>
                        </Paper>
                      </Grid>
                    </>
                  )}
                </Grid>
              </Box>
            )}

            {/* Sezione Magazzino */}
            {selectedSectors.inventory && reportData.inventory && (
              <Box sx={{ mb: 4 }}>
                <Typography variant="h4" gutterBottom color="primary">
                  📦 MAGAZZINO
                </Typography>
                <Grid container spacing={2}>
                  {selectedMetrics.quantities && (
                    <>
                      <Grid item xs={12} sm={4}>
                        <Paper sx={{ p: 2, textAlign: 'center' }}>
                          <Typography variant="h5" color="primary">
                            {reportData.inventory.totalProducts}
                          </Typography>
                          <Typography variant="body2">Prodotti Attivi</Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Paper sx={{ p: 2, textAlign: 'center' }}>
                          <Typography variant="h5" color="warning.main">
                            {reportData.inventory.itemsSold}
                          </Typography>
                          <Typography variant="body2">Articoli Venduti</Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Paper sx={{ p: 2, textAlign: 'center' }}>
                          <Typography variant="h5" color="info.main">
                            {reportData.inventory.itemsUsedMaintenance}
                          </Typography>
                          <Typography variant="body2">Usati in Manutenzioni</Typography>
                        </Paper>
                      </Grid>
                    </>
                  )}
                  {selectedMetrics.revenue && (
                    <Grid item xs={12}>
                      <Paper sx={{ p: 2, textAlign: 'center' }}>
                        <Typography variant="h5" color="success.main">
                          {formatCurrency(reportData.inventory.totalValue)}
                        </Typography>
                        <Typography variant="body2">Valore Totale Magazzino</Typography>
                      </Paper>
                    </Grid>
                  )}
                </Grid>
              </Box>
            )}

            {/* Sezione Meccanici */}
            {selectedSectors.mechanics && reportData.mechanics && (
              <Box sx={{ mb: 4 }}>
                <Typography variant="h4" gutterBottom color="primary">
                  👷 MECCANICI
                </Typography>
                <Grid container spacing={2}>
                  {/* Statistiche Generali */}
                  {selectedMetrics.quantities && (
                    <>
                      <Grid item xs={12} sm={3}>
                        <Paper sx={{ p: 2, textAlign: 'center' }}>
                          <Typography variant="h5" color="primary">
                            {reportData.mechanics.totalMechanics}
                          </Typography>
                          <Typography variant="body2">Meccanici Totali</Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <Paper sx={{ p: 2, textAlign: 'center' }}>
                          <Typography variant="h5" color="success.main">
                            {reportData.mechanics.activeMechanics}
                          </Typography>
                          <Typography variant="body2">Meccanici Attivi</Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <Paper sx={{ p: 2, textAlign: 'center' }}>
                          <Typography variant="h5" color="info.main">
                            {reportData.mechanics.totalWorkSessions}
                          </Typography>
                          <Typography variant="body2">Sessioni di Lavoro</Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <Paper sx={{ p: 2, textAlign: 'center' }}>
                          <Typography variant="h5" color="warning.main">
                            {reportData.mechanics.totalMaintenances}
                          </Typography>
                          <Typography variant="body2">Manutenzioni Lavorate</Typography>
                        </Paper>
                      </Grid>
                    </>
                  )}
                  {/* Ore di Lavoro */}
                  {selectedMetrics.times && (
                    <>
                      <Grid item xs={12} sm={6}>
                        <Paper sx={{ p: 2, textAlign: 'center' }}>
                          <Typography variant="h5" color="success.main">
                            {reportData.mechanics.totalWorkHours}h
                          </Typography>
                          <Typography variant="body2">Ore Totali Lavorate</Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Paper sx={{ p: 2, textAlign: 'center' }}>
                          <Typography variant="h5" color="info.main">
                            {reportData.mechanics.averageHoursPerMechanic}h
                          </Typography>
                          <Typography variant="body2">Ore Medie per Meccanico</Typography>
                        </Paper>
                      </Grid>
                    </>
                  )}
                  {/* Costi Manodopera */}
                  {selectedMetrics.costs && (
                    <>
                      <Grid item xs={12} sm={6}>
                        <Paper sx={{ p: 2, textAlign: 'center' }}>
                          <Typography variant="h5" color="error.main">
                            {formatCurrency(reportData.mechanics.totalLaborCost || 0)}
                          </Typography>
                          <Typography variant="body2">Costo Totale Manodopera</Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Paper sx={{ p: 2, textAlign: 'center' }}>
                          <Typography variant="h5" color="warning.main">
                            {formatCurrency(reportData.mechanics.averageLaborCostPerMechanic || 0)}
                          </Typography>
                          <Typography variant="body2">Costo Medio per Meccanico</Typography>
                        </Paper>
                      </Grid>
                    </>
                  )}
                  {reportData.mechanics.mechanicsDetails && reportData.mechanics.mechanicsDetails.length > 0 && (
                    <Grid item xs={12}>
                      <Paper sx={{ p: 2 }}>
                        <Typography variant="h6" gutterBottom>
                          📊 Dettaglio per Meccanico
                        </Typography>
                        <Box sx={{ overflowX: 'auto' }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                              <tr style={{ backgroundColor: '#f5f5f5' }}>
                                <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'left' }}>Meccanico</th>
                                {selectedMetrics.times && <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'center' }}>Giorni Lavorati</th>}
                                {selectedMetrics.times && <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'center' }}>Ore Totali</th>}
                                {selectedMetrics.quantities && <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'center' }}>Manutenzioni</th>}
                                {selectedMetrics.quantities && <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'center' }}>Sessioni</th>}
                                {selectedMetrics.revenue && <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'center' }}>Tariffa Oraria</th>}
                                {selectedMetrics.revenue && <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'center' }}>Costo Totale</th>}
                              </tr>
                            </thead>
                            <tbody>
                              {reportData.mechanics.mechanicsDetails.map((mechanic, index) => (
                                <React.Fragment key={mechanic.mechanicId}>
                                  <tr style={{ backgroundColor: index % 2 === 0 ? '#fafafa' : 'white' }}>
                                    <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <IconButton 
                                          size="small" 
                                          onClick={() => toggleMechanicExpansion(mechanic.mechanicId)}
                                          sx={{ p: 0.5 }}
                                        >
                                          {expandedMechanics[mechanic.mechanicId] ? <ExpandLess /> : <ExpandMore />}
                                        </IconButton>
                                        <Box>
                                          <strong>{mechanic.mechanicName}</strong>
                                          <br />
                                          <Typography variant="caption" color="text.secondary">
                                            {mechanic.mechanicEmail}
                                          </Typography>
                                        </Box>
                                      </Box>
                                    </td>
                                    {selectedMetrics.times && (
                                      <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'center' }}>
                                        {mechanic.workDays}
                                      </td>
                                    )}
                                    {selectedMetrics.times && (
                                      <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'center' }}>
                                        <strong>{mechanic.totalHours}h</strong>
                                      </td>
                                    )}
                                    {selectedMetrics.quantities && (
                                      <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'center' }}>
                                        {mechanic.totalMaintenances}
                                      </td>
                                    )}
                                    {selectedMetrics.quantities && (
                                      <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'center' }}>
                                        {mechanic.sessionsCount}
                                      </td>
                                    )}
                                    {selectedMetrics.revenue && (
                                      <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'center' }}>
                                        {formatCurrency(mechanic.hourlyRate)}
                                      </td>
                                    )}
                                    {selectedMetrics.revenue && (
                                      <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'center' }}>
                                        <strong>{formatCurrency(mechanic.totalEarnings)}</strong>
                                      </td>
                                    )}
                                  </tr>
                                  {/* Riga espandibile con dettagli giornalieri */}
                                  <tr>
                                    <td colSpan={6} style={{ padding: 0, border: 'none' }}>
                                      <Collapse in={expandedMechanics[mechanic.mechanicId]} timeout="auto" unmountOnExit>
                                        <Box sx={{ p: 2, bgcolor: '#f8f9fa', border: '1px solid #ddd' }}>
                                          <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                                            📅 Dettaglio Giornaliero - {mechanic.mechanicName}
                                          </Typography>
                                          {mechanic.dailyDetails && mechanic.dailyDetails.length > 0 ? (
                                            <Box sx={{ overflowX: 'auto' }}>
                                              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                                <thead>
                                                  <tr style={{ backgroundColor: '#e9ecef' }}>
                                                    <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'left' }}>Data</th>
                                                    <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'center' }}>Ore Totali</th>
                                                    <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'center' }}>Manutenzioni</th>
                                                    <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'center' }}>Sessioni</th>
                                                    <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'center' }}>Tariffa Oraria</th>
                                                    <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'center' }}>Costo Totale</th>
                                                  </tr>
                                                </thead>
                                                <tbody>
                                                  {mechanic.dailyDetails.map((day, dayIndex) => (
                                                    <React.Fragment key={day.date}>
                                                      <tr style={{ backgroundColor: dayIndex % 2 === 0 ? '#ffffff' : '#f8f9fa' }}>
                                                        <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <IconButton 
                                                              size="small" 
                                                              onClick={() => toggleDayExpansion(mechanic.mechanicId, day.date)}
                                                              sx={{ p: 0.5 }}
                                                            >
                                                              {expandedDays[`${mechanic.mechanicId}-${day.date}`] ? <ExpandLess /> : <ExpandMore />}
                                                            </IconButton>
                                                            <strong>{new Date(day.date).toLocaleDateString('it-IT')}</strong>
                                                          </Box>
                                                        </td>
                                                        <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'center' }}>
                                                          <strong>{day.totalHours}h</strong>
                                                        </td>
                                                        <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'center' }}>
                                                          {day.maintenancesCount}
                                                        </td>
                                                        <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'center' }}>
                                                          {day.sessionsCount}
                                                        </td>
                                                        <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'center' }}>
                                                          {formatCurrency(day.hourlyRate)}
                                                        </td>
                                                        <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'center' }}>
                                                          <strong>{formatCurrency(day.totalCost)}</strong>
                                                        </td>
                                                      </tr>
                                                      {/* Riga espandibile con dettagli manutenzioni */}
                                                      <tr>
                                                        <td colSpan={6} style={{ padding: 0, border: 'none' }}>
                                                          <Collapse in={expandedDays[`${mechanic.mechanicId}-${day.date}`]} timeout="auto" unmountOnExit>
                                                            <Box sx={{ p: 2, bgcolor: '#f0f8ff', border: '1px solid #ddd', ml: 2 }}>
                                                              <Typography variant="subtitle1" gutterBottom sx={{ mb: 2, fontWeight: 'bold' }}>
                                                                🔧 Manutenzioni del {new Date(day.date).toLocaleDateString('it-IT')}
                                                              </Typography>
                                                              {day.maintenancesDetails && day.maintenancesDetails.length > 0 ? (
                                                                <Box sx={{ overflowX: 'auto' }}>
                                                                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                                                    <thead>
                                                                      <tr style={{ backgroundColor: '#e3f2fd' }}>
                                                                        <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'left' }}>Protocollo</th>
                                                                        <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'left' }}>Descrizione</th>
                                                                        <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'center' }}>Stato</th>
                                                                        <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'center' }}>Sessioni</th>
                                                                        <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'center' }}>Ore</th>
                                                                        <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'center' }}>Costo</th>
                                                                        <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'center' }}>Azioni</th>
                                                                      </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                      {day.maintenancesDetails.map((maintenance, maintenanceIndex) => {
                                                                        const isDescriptionExpanded = expandedDescriptions[maintenance.id];
                                                                        const truncatedDescription = maintenance.description.length > 30 
                                                                          ? maintenance.description.substring(0, 30) + '...' 
                                                                          : maintenance.description;
                                                                        
                                                                        return (
                                                                          <tr key={maintenance.id} style={{ backgroundColor: maintenanceIndex % 2 === 0 ? '#ffffff' : '#f8f9fa' }}>
                                                                                 <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                                                                                   <strong>{maintenance.protocolNumber}</strong>
                                                                                 </td>
                                                                            <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                                                                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                                <span>
                                                                                  {isDescriptionExpanded ? maintenance.description : truncatedDescription}
                                                                                </span>
                                                                                {maintenance.description.length > 30 && (
                                                                                  <IconButton 
                                                                                    size="small" 
                                                                                    onClick={() => toggleDescriptionExpansion(maintenance.id)}
                                                                                    sx={{ p: 0.5, color: 'text.secondary' }}
                                                                                    title={isDescriptionExpanded ? "Riduci descrizione" : "Espandi descrizione"}
                                                                                  >
                                                                                    {isDescriptionExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                                                                                  </IconButton>
                                                                                )}
                                                                              </Box>
                                                                            </td>
                                                                            <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'center' }}>
                                                                              <Chip 
                                                                                label={maintenance.status} 
                                                                                size="small"
                                                                                color={maintenance.status === 'completed' ? 'success' : maintenance.status === 'in_progress' ? 'warning' : 'default'}
                                                                              />
                                                                            </td>
                                                                            <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'center' }}>
                                                                              {maintenance.sessionsCount}
                                                                            </td>
                                                                            <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'center' }}>
                                                                              <strong>{maintenance.totalHours}h</strong>
                                                                            </td>
                                                                            <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'center' }}>
                                                                              <strong>{formatCurrency(maintenance.cost)}</strong>
                                                                            </td>
                                                                            <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'center' }}>
                                                                              <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                                                                                <IconButton 
                                                                                  size="small" 
                                                                                  onClick={() => handleOpenMaintenanceDetails(maintenance.id)}
                                                                                  sx={{ p: 0.5, color: 'primary.main' }}
                                                                                  title="Visualizza dettagli"
                                                                                >
                                                                                  <Visibility fontSize="small" />
                                                                                </IconButton>
                                                                                <IconButton 
                                                                                  size="small" 
                                                                                  onClick={() => handleOpenMaintenanceRepair(maintenance.id)}
                                                                                  sx={{ p: 0.5, color: 'warning.main' }}
                                                                                  title="Apri riparazione"
                                                                                >
                                                                                  <Build fontSize="small" />
                                                                                </IconButton>
                                                                              </Box>
                                                                            </td>
                                                                          </tr>
                                                                        );
                                                                      })}
                                                                    </tbody>
                                                                  </table>
                                                                </Box>
                                                              ) : (
                                                                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                                                  Nessuna manutenzione lavorata in questo giorno.
                                                                </Typography>
                                                              )}
                                                            </Box>
                                                          </Collapse>
                                                        </td>
                                                      </tr>
                                                    </React.Fragment>
                                                  ))}
                                                </tbody>
                                              </table>
                                            </Box>
                                          ) : (
                                            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                              Nessun dato giornaliero disponibile per questo meccanico nel periodo selezionato.
                                            </Typography>
                                          )}
                                        </Box>
                                      </Collapse>
                                    </td>
                                  </tr>
                                </React.Fragment>
                              ))}
                            </tbody>
                          </table>
                        </Box>
                      </Paper>
                    </Grid>
                  )}
                </Grid>
              </Box>
            )}

            {/* Riepilogo Finanziario */}
            {(selectedMetrics.revenue || selectedMetrics.margins || selectedMetrics.costs) && (
              <Box sx={{ mb: 4 }}>
                <Typography variant="h4" gutterBottom color="success.main">
                  💰 RIEPILOGO FINANZIARIO
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Paper sx={{ p: 3, bgcolor: 'success.light', color: 'success.contrastText' }}>
                      <Typography variant="h5" textAlign="center">
                        Fatturato Totale (esclusa IVA 22%): {formatCurrency(
                          (reportData.maintenance?.totalRevenue || 0) + (reportData.sales?.totalRevenue || 0) + (reportData.rentals?.totalRevenue || 0)
                        )}
                      </Typography>
                      <Typography variant="body2" textAlign="center" sx={{ mt: 1, opacity: 0.8 }}>
                        Con IVA: {formatCurrency(
                          ((reportData.maintenance?.totalRevenueWithVAT || 0) + (reportData.sales?.totalRevenueWithVAT || 0) + (reportData.rentals?.totalRevenueWithVAT || 0))
                        )}
                      </Typography>
                    </Paper>
                  </Grid>
                  {selectedMetrics.costs && (
                    <Grid item xs={12} sm={6}>
                      <Paper sx={{ p: 3, bgcolor: 'error.light', color: 'error.contrastText' }}>
                        <Typography variant="h5" textAlign="center">
                          Costi Totali: {formatCurrency(
                            (reportData.maintenance?.totalCosts || 0) + (reportData.sales?.totalCosts || 0)
                          )}
                        </Typography>
                      </Paper>
                    </Grid>
                  )}
                  {selectedMetrics.margins && (
                    <Grid item xs={12}>
                      <Paper sx={{ 
                        p: 3, 
                        bgcolor: ((reportData.maintenance?.totalMargins || 0) + (reportData.sales?.totalMargins || 0)) >= 0 ? 'success.main' : 'error.main',
                        color: 'white'
                      }}>
                        <Typography variant="h4" textAlign="center">
                          Margine Totale Periodo: {formatCurrency(
                            (reportData.maintenance?.totalMargins || 0) + (reportData.sales?.totalMargins || 0)
                          )}
                        </Typography>
                        <Typography variant="body1" textAlign="center" sx={{ mt: 1, opacity: 0.8 }}>
                          (Fatturato esclusa IVA - Costi)
                        </Typography>
                      </Paper>
                    </Grid>
                  )}
                </Grid>
              </Box>
            )}
          </div>
        </DialogContent>

        <DialogActions sx={{ '@media print': { display: 'none' } }}>
          <Button onClick={handleClose}>Chiudi</Button>
        </DialogActions>
      </Dialog>
      
      {/* Maintenance Details Dialog */}
      <MaintenanceDetails
        open={maintenanceDetailsOpen}
        handleClose={handleCloseMaintenanceDetails}
        maintenanceId={selectedMaintenanceId}
        laborCost={0} // Non necessario per la visualizzazione
      />
      
      {/* Maintenance Repair Dialog */}
      <MaintenanceRepair
        open={maintenanceRepairOpen}
        handleClose={handleCloseMaintenanceRepair}
        maintenanceId={selectedMaintenanceId}
        onUpdate={() => {
          // Ricarica i dati del report se necessario
          console.log('Manutenzione aggiornata');
        }}
      />
      
    </LocalizationProvider>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={it}>
      {/* Dialog principale - si nasconde quando reportDialogOpen è false */}
      <Dialog
        open={open && reportDialogOpen}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Preview sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h5">Genera Report Personalizzato</Typography>
          </Box>
        </DialogTitle>

        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Grid container spacing={3}>
            {/* Selezione Settori */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2, height: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">Settori da Analizzare</Typography>
                  <Chip 
                    label={getSelectedCount(selectedSectors)} 
                    size="small" 
                    color="primary" 
                    sx={{ ml: 2 }}
                  />
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {sectors.map((sector) => (
                    <React.Fragment key={sector.key}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={selectedSectors[sector.key]}
                            onChange={() => handleSectorChange(sector.key)}
                            color="primary"
                          />
                        }
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <span style={{ marginRight: 8, fontSize: '1.2em' }}>{sector.icon}</span>
                            {sector.label}
                          </Box>
                        }
                      />
                      {sector.key === 'mechanics' && user?.role === ROLES.SUPERADMIN && (
                        <Button
                          variant="outlined"
                          color="primary"
                          size="small"
                          startIcon={<SmartToy />}
                          onClick={handleOpenAiChat}
                          sx={{ ml: 4, mb: 1, alignSelf: 'flex-start' }}
                        >
                          Report AI
                        </Button>
                      )}
                    </React.Fragment>
                  ))}
                </Box>
              </Paper>
            </Grid>

            {/* Selezione Metriche */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2, height: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">Metriche da Includere</Typography>
                  <Chip 
                    label={getSelectedCount(selectedMetrics)} 
                    size="small" 
                    color="secondary" 
                    sx={{ ml: 2 }}
                  />
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {metrics.map((metric) => (
                    <FormControlLabel
                      key={metric.key}
                      control={
                        <Checkbox
                          checked={selectedMetrics[metric.key]}
                          onChange={() => handleMetricChange(metric.key)}
                          color="secondary"
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <span style={{ marginRight: 8, fontSize: '1.2em' }}>{metric.icon}</span>
                          {metric.label}
                        </Box>
                      }
                    />
                  ))}
                </Box>
              </Paper>
            </Grid>

            {/* Selezione Periodo */}
            <Grid item xs={12}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>Periodo di Analisi</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <DatePicker
                      label="Data Inizio"
                      value={dateRange.startDate}
                      onChange={(newValue) => setDateRange(prev => ({ ...prev, startDate: newValue }))}
                      renderInput={(params) => <TextField {...params} fullWidth />}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <DatePicker
                      label="Data Fine"
                      value={dateRange.endDate}
                      onChange={(newValue) => setDateRange(prev => ({ ...prev, endDate: newValue }))}
                      renderInput={(params) => <TextField {...params} fullWidth />}
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleClose} disabled={loading}>
            Annulla
          </Button>
          <Button
            variant="contained"
            onClick={handleGeneratePreview}
            disabled={loading || (!Object.values(selectedSectors).some(Boolean)) || (!Object.values(selectedMetrics).some(Boolean))}
            startIcon={<Preview />}
            sx={{ minWidth: 140 }}
          >
            {loading ? 'Caricando...' : 'Anteprima'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Maintenance Details Dialog */}
      <MaintenanceDetails
        open={maintenanceDetailsOpen}
        handleClose={handleCloseMaintenanceDetails}
        maintenanceId={selectedMaintenanceId}
        laborCost={0} // Non necessario per la visualizzazione
      />
      
      {/* Maintenance Repair Dialog */}
      <MaintenanceRepair
        open={maintenanceRepairOpen}
        handleClose={handleCloseMaintenanceRepair}
        maintenanceId={selectedMaintenanceId}
        onUpdate={() => {
          // Ricarica i dati del report se necessario
          console.log('Manutenzione aggiornata');
        }}
      />
      
    </LocalizationProvider>
  );
};

export default ReportDialog; 